import type { TaxonomyUrn } from "@shared/types/taxonomy.ts";
import type { CorpusCatalogueSubject } from "@shared/types/corpus/api.ts";
import { ROUTES } from "../route_paths";
import type { TaxonomyView } from "./taxonomyView";

/** One health field of the corpus catalogue. */
export interface CatalogueField {
  urn: TaxonomyUrn;
  /** The field's name; its URN when the taxonomy no longer knows it. */
  name: string;
  papers: number;
  papersText: string;
}

/** The health fields of the catalogue by name, the one with the most papers first. */
export function listCatalogueFields(
  byField: Record<string, number>,
  taxonomy: TaxonomyView
): CatalogueField[] {
  return Object.entries(byField)
    .map(([urn, papers]) => ({
      urn: urn as TaxonomyUrn,
      name: taxonomy.findField(urn as TaxonomyUrn)?.enName ?? urn,
      papers,
      papersText: papers.toLocaleString(),
    }))
    .sort((a, b) => b.papers - a.papers || a.name.localeCompare(b.name));
}

/** The fields whose name contains the search text, ignoring case; all of them for blank text. */
export function filterCatalogueFields(fields: CatalogueField[], query: string): CatalogueField[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return fields;
  return fields.filter((field) => field.name.toLowerCase().includes(needle));
}

/** A subject's name for a person: its readable name, else its id, so no subject is ever dropped. */
export function nameCatalogueSubject(subject: CorpusCatalogueSubject): string {
  return subject.name ?? subject.subject;
}

/** What the catalogue narrows the Documents list to when it sends the operator there. */
export interface CatalogueDocumentsFilter {
  field?: TaxonomyUrn | null;
  subject?: string;
}

/** The Documents list of the papers a catalogue count is made of: the ingested ones, with the
 *  field and the main subject the count was for. */
export function buildCatalogueDocumentsPath({ field, subject }: CatalogueDocumentsFilter): string {
  const params = new URLSearchParams({ status: "ingested" });
  if (field) params.set("fields", field);
  if (subject) params.set("subject", subject);
  return `${ROUTES.internal.library.catalogue.documents.path()}?${params.toString()}`;
}

/** The Subjects page narrowed to one health field. */
export function buildCatalogueSubjectsPath(field: TaxonomyUrn): string {
  return `${ROUTES.internal.library.catalogue.subjects.path()}?field=${encodeURIComponent(field)}`;
}
